/**
 * Contact Form Utilities
 *
 * Data transformation and utilities for the unified ContactForm component.
 * Handles both snake_case (database) and camelCase (enriched data) formats.
 */

import type { ContactRow, ContactStatus, PreferredContactMethod } from '@/types/contacts'
import type { ContactFormInput } from './contact-form-schema'
import { contactFormDefaults } from './contact-form-schema'

/**
 * camelCase format used by enriched data (hangar-view)
 */
export interface ContactFormInitialData {
  firstName: string
  lastName: string
  jobTitle?: string | null
  emailPrimary: string
  emailSecondary?: string | null
  phoneOffice?: string | null
  phoneMobile?: string | null
  phoneOther?: string | null
  isPrimaryContact: boolean
  isBillingContact: boolean
  isOperationsContact: boolean
  preferredContactMethod: PreferredContactMethod
  status: ContactStatus
  notes?: string | null
}

/**
 * Convert ContactRow (snake_case database format) to form defaults
 */
export function contactRowToFormDefaults(contact: ContactRow): ContactFormInput {
  return {
    first_name: contact.first_name,
    last_name: contact.last_name,
    job_title: contact.job_title || '',
    email_primary: contact.email_primary,
    email_secondary: contact.email_secondary || '',
    phone_office: contact.phone_office || '',
    phone_mobile: contact.phone_mobile || '',
    phone_other: contact.phone_other || '',
    is_primary_contact: contact.is_primary_contact,
    is_billing_contact: contact.is_billing_contact,
    is_operations_contact: contact.is_operations_contact,
    preferred_contact_method: contact.preferred_contact_method,
    status: contact.status,
    notes: contact.notes || '',
  }
}

/**
 * Convert camelCase initialData to form defaults
 */
export function initialDataToFormDefaults(data: ContactFormInitialData): ContactFormInput {
  return {
    first_name: data.firstName,
    last_name: data.lastName,
    job_title: data.jobTitle || '',
    email_primary: data.emailPrimary,
    email_secondary: data.emailSecondary || '',
    phone_office: data.phoneOffice || '',
    phone_mobile: data.phoneMobile || '',
    phone_other: data.phoneOther || '',
    is_primary_contact: data.isPrimaryContact,
    is_billing_contact: data.isBillingContact,
    is_operations_contact: data.isOperationsContact,
    preferred_contact_method: data.preferredContactMethod,
    status: data.status,
    notes: data.notes || '',
  }
}

/**
 * Normalize either format to form defaults
 */
export function normalizeContactData(
  contact?: ContactRow | null,
  initialData?: ContactFormInitialData | null
): ContactFormInput {
  if (contact) {
    return contactRowToFormDefaults(contact)
  }
  if (initialData) {
    return initialDataToFormDefaults(initialData)
  }
  return contactFormDefaults
}

/**
 * Field name mapping for prefocusField
 * Maps camelCase field names to snake_case form field names
 */
export const CONTACT_PREFOCUS_FIELD_MAP: Record<string, keyof ContactFormInput> = {
  firstName: 'first_name',
  lastName: 'last_name',
  jobTitle: 'job_title',
  email: 'email_primary',
  emailPrimary: 'email_primary',
  emailSecondary: 'email_secondary',
  phone: 'phone_mobile',
  phoneOffice: 'phone_office',
  phoneMobile: 'phone_mobile',
  phoneOther: 'phone_other',
  status: 'status',
  notes: 'notes',
  // Also support snake_case directly
  first_name: 'first_name',
  last_name: 'last_name',
  email_primary: 'email_primary',
  phone_mobile: 'phone_mobile',
}
